// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { Text } from '@mysten/ui';
import { useState } from 'react';
import clsx from 'clsx';

import { ImageIcon } from '~/ui/ImageIcon';
import { StakeColumn } from '~/components/top-validators-card/StakeColumn';
import { formatDailyYieldPercent, shortenAddress } from '~/utils/explorerFormat';

export type ValidatorOption = {
	address: string;
	name?: string;
	imageUrl?: string;
	stake?: string | number | bigint;
	apy?: number | null;
	apyEstimated?: boolean;
	commission?: number | null;
};

function normalizeValidators(validators: string[] | ValidatorOption[]): ValidatorOption[] {
	return validators.map((v) =>
		typeof v === 'string' ? { address: v } : v,
	);
}

export function SelectValidatorModal({
	isOpen,
	onClose,
	validators,
	onSelect,
}: {
	isOpen: boolean;
	onClose: () => void;
	validators: string[] | ValidatorOption[];
	onSelect: (validator: string) => void;
}) {
	const [searchTerm, setSearchTerm] = useState('');

	if (!isOpen) return null;

	const options = normalizeValidators(validators);
	const filtered = options.filter((v) => {
		const q = searchTerm.toLowerCase();
		return (
			v.address.toLowerCase().includes(q) ||
			(v.name?.toLowerCase().includes(q) ?? false)
		);
	});

	return (
		<div
			className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
			onClick={onClose}
		>
			<div
				className="bg-[#1E222F] border border-[#1E293B] rounded-2xl shadow-2xl w-full max-w-lg p-7 text-white"
				onClick={(e) => e.stopPropagation()}
			>
				<h3 className="mb-6 text-center text-lg font-bold tracking-wide">选择验证者</h3>

				<input
					type="text"
					placeholder="搜索名称或地址…"
					className="w-full p-3 mb-5 bg-[#0F172A] border border-[#1E293B] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>

				<div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
					{filtered.length > 0 ? (
						filtered.map((v) => (
							<button
								key={v.address}
								type="button"
								className={clsx(
									'p-4 bg-[#0F172A]/40 border border-[#1E293B]/60 rounded-xl text-left transition-all',
									'hover:bg-[#38BDF8]/10 hover:border-[#38BDF8]/50',
								)}
								onClick={() => onSelect(v.address)}
							>
								<div className="flex items-center gap-3">
									{v.imageUrl || v.name ? (
										<ImageIcon
											src={v.imageUrl}
											size="sm"
											fallback={v.name ?? v.address}
											label={v.name ?? v.address}
											circle
										/>
									) : null}
									<div className="min-w-0 flex-1">
										<div className="font-semibold text-white truncate">
											{v.name ?? shortenAddress(v.address)}
										</div>
										<div className="flex flex-wrap gap-x-2 text-xs text-[#aeb9d0] mt-0.5">
											{v.stake != null ? (
												<span>
													池 <StakeColumn stake={v.stake} hideCoinSymbol onDark />
												</span>
											) : null}
											{v.apy != null ? (
												<span>日化 {formatDailyYieldPercent(v.apy, { estimated: v.apyEstimated })}</span>
											) : null}
											{v.commission != null ? (
												<span>佣金 {v.commission.toFixed(2)}%</span>
											) : null}
										</div>
										{v.name ? (
											<div className="font-mono text-[10px] text-[#64748b] truncate mt-0.5">
												{v.address}
											</div>
										) : null}
									</div>
								</div>
							</button>
						))
					) : (
						<Text variant="pBody/normal" className="text-gray-500 text-center py-8">
							没有找到匹配的验证者
						</Text>
					)}
				</div>

				<div className="flex justify-end mt-6 pt-4 border-t border-[#1E293B]/60">
					<button
						type="button"
						onClick={onClose}
						className="px-6 py-2 rounded-xl border border-[#1E293B] text-[#AEB9D0] text-sm font-semibold hover:bg-[#1E293B]"
					>
						取消
					</button>
				</div>
			</div>
		</div>
	);
}
